import React, { useCallback, useEffect, useState } from 'react';
import { Create, useDataProvider, useGetOne, useNotify, useRedirect } from 'react-admin';
import Permission from '../../components/Permission';
import WizardForm from '../../components/WizardForm';
import { PERMISSIONS } from '../../constants';

import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { getChainId } from '../../lib';
import FormCustomerGroup from './wizardForms/FormCustomerGroup';
import FormScoringType from './wizardForms/FormScoringType';
import FormFuelScoring from './wizardForms/FormFuelScoring';
import FormScheduleScoring from './wizardForms/FormScheduleScoring';
import FormSummary from './wizardForms/FormSummary';
import FormCity from './wizardForms/FormCity';

import { Typography } from '@mui/material';
import { uniq } from 'lodash';
import { DayScheduledScoring } from '../../models/dayScheduledScoring';
import { Scoring } from '../../models/scoring';
import { FuelScoring } from '../../models/fuelScoring';
import FormNotification from './wizardForms/FormNotification';

const ScoringCreate: React.FC = () => {
  const { state: params }: { state: Object } = useLocation();
  const [chainId] = useState(getChainId());
  if (!chainId) { return <Typography>Selecione uma Rede</Typography>; }

  const { data: chain } = useGetOne('chains', { id: chainId });

  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fuels, setFuels] = useState<{ [k: string]: any }[]>([]);
  const [completedSteps, setCompletedSteps] = React.useState<{
    [k: number]: boolean;
  }>({});

  useEffect(() => {
    // @ts-ignore
    dataProvider.getList('fuels', { sort: { field: 'name', order: 'ASC' }, pagination: { page: 1, perPage: 9999 } })
      .then(({ data }) => {
        setFuels(data as unknown as { [k: string]: any; }[]);
      });
  }, [dataProvider]);

  const validateScoringType = (formData: any) => {
    if (formData.type === 'special' && !formData.description) {
      return { error: true, message: 'Campo de descrição está vazio' };
    }
    return { error: false };
  };

  const handleScoringType = (formData: any, nextStep: (index?: number) => void) => {
    nextStep();
  };

  const validateFuelScoring = (formData: any) => {
    if ((!formData.stationIds || !formData.stationIds?.length) && !formData.selectsAllPlaces) {
      return { error: true, message: 'Campo de postos está vazio' };
    }
    if (!formData.fuelScorings || !formData.fuelScorings.filter((fs: any) => fs.factor !== undefined && fs.factor !== null && fs.factor >= 0).length) {
      return { error: true, message: 'Nenhum fator de pontuação foi definido' };
    }
    if (formData.intervalInHours && !formData.usageLimit) {
      return { error: true, message: 'Defina um limite de uso ou remova o intervalo de uso' };
    }
    return { error: false };
  };

  const handleFuelScoring = (formData: any, nextStep: (index?: number) => void) => {
    if (formData.type === 'default') {
      nextStep(5);
    } else {
      nextStep();
    }
  };

  const validateScheduleScoring = (formData: any) => {
    if (formData.isSchedule) {
      if (!formData.startAt || !formData.endAt) {
        return { error: true, message: 'Defina uma data de início e fim' };
      } else if (!moment(formData.startAt).isValid() || !moment(formData.endAt).isValid()) {
        return { error: true, message: 'Defina uma data válida' };
      } else if (moment(formData.endAt) <= moment(formData.startAt)) {
        return { error: true, message: 'Data final não pode ser inferior a data inicial' };
      }

      if (!formData.frequency) {
        return { error: true, message: 'Defina o tipo de frequência' };
      }

      if (formData.frequency === 'daily' && (!formData.startTime || !formData.endTime)) {
        return { error: true, message: 'Defina um horário de início e fim' };
      } else if (formData.frequency === 'daily' && (formData.startTime && formData.endTime) && moment(formData.endTime) <= moment(formData.startTime)) {
        return { error: true, message: 'Horário de fim não pode ser inferior ao horário de início' };
      }

      if (formData.frequency === 'weekly' && (!formData.weekSchedual || formData.weekSchedual.filter((ws: any) => ws.isActive && (!ws.startTime || !ws.endTime)).length)) {
        return { error: true, message: 'Defina um horário de início e fim' };
      } else if (formData.frequency === 'weekly' && formData.weekSchedual && formData.weekSchedual.filter((ws: any) => ws.isActive && moment(ws.endTime) <= moment(ws.startTime)).length) {
        return { error: true, message: 'Horário de fim não pode ser inferior ao horário de início' };
      }
    }

    return { error: false };
  };

  const handleScheduleScoring = (formData: any, nextStep: () => void) => {
    nextStep();
  };

  const validateCustomerGroup = (formData: any) => {
    if (formData.type !== 'special') {
      return { error: false };
    }

    // Zeragem = pontuação especial com todos os fatores em 0. O transactions-api só aplica a zeragem
    // quando ela está segmentada por etiqueta ou clientes específicos (que têm prioridade sobre o público);
    // cidade NÃO vira prioridade — uma promoção pública sobreporia a zeragem — então não vale aqui.
    const isZeroingScoring = (formData.fuelScorings?.length ?? 0) > 0
      && formData.fuelScorings.every((fs: any) => !fs.factor);
    const hasCustomerSegmentation = !!(formData.customerTagIds?.length || formData.customerIds);

    if (isZeroingScoring && !hasCustomerSegmentation) {
      return { error: true, message: 'Para zerar a pontuação, selecione um grupo específico (etiqueta de clientes ou clientes).' };
    }
    return { error: false };
  };

  const validateCity = (formData: any) => {
    return { error: false };
  };

  const validateNotification = (formData: any) => {
    if (formData.type === 'special' && formData.hasNotification) {
      if (!formData.notification?.title) {
        return { error: true, message: 'Defina um título para notificação' };
      }
      if (!formData.notification?.body) {
        return { error: true, message: 'Defina uma descrição para notificação' };
      }
      if (!formData.notification?.pushNotification && !formData.notification?.sms) {
        return { error: true, message: 'Defina um método de notificação' };
      }
      if (formData.notification.pushNotification && !formData.notification?.pushMessage) {
        return { error: true, message: 'Defina uma mensagem push' };
      }
      if (formData.notification.sms && !formData.notification?.smsMessage) {
        return { error: true, message: 'Defina uma mensagem sms' };
      }
      if (formData.notification?.sendNow !== true && !(moment(formData.notification?.sendAt) > moment(Date.now()).add(10, 'minutes'))) {
        return { error: true, message: 'Defina um horário de envio válido' };
      }
    }
    return { error: false };
  };

  const handleCustomerGroup = (formData: any, nextStep: () => void) => {
    nextStep();
  };

  const handleCity = (formData: any, nextStep: () => void) => {
    nextStep();
  };

  const handleNotification = (formData: any, nextStep: () => void) => {
    nextStep();
  };

  const handleConcluedStep = (index: number) => {
    setCompletedSteps(value => ({ ...value, [index]: true }));
  };

  const handleCreateScoring = (formData: any) => {
    setIsLoading(true);

    // Promoção (algum fator > 0): envia só os fatores positivos — os combustíveis em 0 caem na pontuação padrão.
    // Zeragem (todos os fatores 0): envia todos os combustíveis a 0 para sobrepor a padrão e zerar o grupo.
    // Sem essa distinção, um `>= 0` cego persistiria os combustíveis não-promovidos (default 0) e os zeraria.
    const positiveFuelScorings = formData.fuelScorings
      .filter((fs: any) => fs.factor !== undefined && fs.factor !== null && fs.factor > 0);
    const isZeroingScoring = positiveFuelScorings.length === 0;

    const fuelScoringsToSend = (isZeroingScoring ? formData.fuelScorings : positiveFuelScorings)
      .filter((fs: any) => fs.factor !== undefined && fs.factor !== null && fs.factor >= 0)
      .map((fs: any) => ({
        fuelId: fs.id,
        factor: fs.factor
      }));

    dataProvider.create('scorings', {
      data: {
        ...Scoring.createScoring(formData),
        fuelScorings: fuelScoringsToSend,
      }
    }).then(response => {
      notify('Pontuação criada com sucesso');
      redirect('/scorings');
    }).catch(error => {
      notify(error.message || 'Erro ao criar pontuação', { type: 'warning' });
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const getInitialValues = useCallback(() => {
    const { fuelScorings: paramsFuelScorings, placeScorings: paramsPlaceScorings, dayScheduledScorings: paramsDayScheduledScorings, _scrollToTop, ...restParams } = params as { fuelScorings: FuelScoring[], dayScheduledScorings: DayScheduledScoring[], [k: string]: any } || {};

    const stationIds = uniq(paramsPlaceScorings?.map((ps: any) => ps.placeId));

    const fuelScorings = (fuels ? Object.values(fuels) : []).map(fuel => {
      const existingScoring = paramsFuelScorings?.find((fs: any) => fs.fuelId === fuel.id);
      return {
        ...fuel,
        factor: existingScoring?.factor ?? 0,
      };
    });

    const dayScheduledScorings = Array(7).fill({}).map((_, index) => (
      paramsDayScheduledScorings?.find((dayS: any) => dayS.dayOfTheWeek === index) || { dayOfTheWeek: index, isActive: false }
    ));

    // Calcula isSchedule baseado nos campos de agendamento
    const isSchedule = !!(restParams.startAt || restParams.endAt || restParams.frequency);

    return {
      type: 'default',
      chainId,
      stationIds,
      fuelScorings,
      weekSchedual: dayScheduledScorings,
      ...restParams,
      isSchedule,
    };
  }, [fuels, chainId, params]);

  return (
    <Permission permission={PERMISSIONS.SCORINGS}>
      <Create title='Cadastrar pontuação'>
        <WizardForm
          sanitizeEmptyValues
          isLoading={isLoading}
          completedSteps={completedSteps}
          defaultValues={getInitialValues()}
          handleConcluedStep={handleConcluedStep}
          formSteps={[
            {
              label: "Pontuação",
              component: <FormScoringType />,
              validate: validateScoringType,
              onNextFunction: handleScoringType,
            },
            {
              label: "Postos e Combustíveis",
              component: <FormFuelScoring fuels={fuels} />,
              validate: validateFuelScoring,
              onNextFunction: handleFuelScoring,
            },
            {
              label: "Agendamento",
              component: <FormScheduleScoring />,
              validate: validateScheduleScoring,
              onNextFunction: handleScheduleScoring,
              disableStep: (data) => {
                return data.type === 'default';
              }
            },
            {
              label: "Cidades",
              component: <FormCity />,
              validate: validateCity,
              onNextFunction: handleCity,
              disableStep: (data) => {
                return data.type === 'default';
              }
            },
            {
              label: "Clientes",
              component: <FormCustomerGroup />,
              validate: validateCustomerGroup,
              onNextFunction: handleCustomerGroup,
              disableStep: (data) => {
                return data.type === 'default';
              }
            },
            {
              label: "Notificação",
              component: <FormNotification />,
              validate: validateNotification,
              onNextFunction: handleNotification,
              disableStep: (data) => {
                return data.type === 'default';
              }
            },
            {
              label: "Confirmação",
              component: <FormSummary />,
              onNextFunction: handleCreateScoring,
              validate: (formData) => {
                for (const validate of [validateScoringType, validateFuelScoring, validateScheduleScoring, validateCustomerGroup]) {
                  const result = validate(formData);
                  if (result.error === true) {
                    return result;
                  }
                }
                return { error: false };
              },
              disableStep: (data, completedSteps) => {
                if (data.type === 'default') {
                  return [0, 1].some(index => !completedSteps[index] || completedSteps[index] === false);
                } else if (data.type === 'special') {
                  return [0, 1, 2, 3].some(index => !completedSteps[index] || completedSteps[index] === false);
                }
                return false;
              }
            },
          ]}
        />
      </Create>
    </Permission>
  );
};

export default ScoringCreate;
